import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLocation } from "wouter";
import { Gift, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";

export default function RedeemPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  // Check for gift card number in URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const cardParam = urlParams.get('card');
  
  const [giftCardNumber, setGiftCardNumber] = useState(cardParam || "");
  const [isChecking, setIsChecking] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [cardStatus, setCardStatus] = useState<any>(null);
  const [redemptionSuccess, setRedemptionSuccess] = useState(false);

  // Fetch user's redemption history
  const { data: redemptionsData } = useQuery<{ success: boolean; redemptions: any[] }>({
    queryKey: ['/api/cardivo/gift-cards/redemptions'],
    enabled: !!user,
  });

  const handleCheckStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!giftCardNumber.trim()) {
      toast({
        title: "Error",
        description: "Please enter a gift card number",
        variant: "destructive",
      });
      return;
    }

    setIsChecking(true);
    setCardStatus(null);

    try {
      const response = await apiRequest("POST", "/api/cardivo/gift-cards/status", {
        giftCardNumber: giftCardNumber.trim().toUpperCase(),
      });

      setCardStatus(response);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to check gift card status",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleRedeem = async () => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please log in to redeem your gift card",
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }

    setIsRedeeming(true);

    try {
      const response: any = await apiRequest("POST", "/api/cardivo/gift-cards/redeem", {
        giftCardNumber: giftCardNumber.trim().toUpperCase(),
      });

      setRedemptionSuccess(true);
      toast({
        title: "Success!",
        description: response.message || "Gift card redeemed successfully",
      });

      // Clear form and invalidate query to refresh history
      setGiftCardNumber("");
      setCardStatus(null);
      
      // Refresh redemption history
      await queryClient.invalidateQueries({ queryKey: ['/api/cardivo/gift-cards/redemptions'] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to redeem gift card",
        variant: "destructive",
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-blue-900">
      <Navigation />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Redeem Gift Card
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Enter your Cardivo gift card number to redeem it at SnuggleCrafters
          </p>
        </div>

        {/* Redemption Success Message */}
        {redemptionSuccess && (
          <Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-900/20">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Your gift card has been successfully redeemed! The value has been added to your account.
            </AlertDescription>
          </Alert>
        )}

        {/* Redemption Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Enter Gift Card Number</CardTitle>
            <CardDescription>
              Enter your Cardivo gift card number to check its status and redeem it
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCheckStatus} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="giftCardNumber" data-testid="label-gift-card-number">
                  Gift Card Number
                </Label>
                <Input
                  id="giftCardNumber"
                  type="text"
                  placeholder="CARDIVO04921624"
                  value={giftCardNumber}
                  onChange={(e) => setGiftCardNumber(e.target.value.toUpperCase())}
                  disabled={isChecking || isRedeeming}
                  className="font-mono"
                  data-testid="input-gift-card-number"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your gift card number can be found on your Cardivo gift card
                </p>
              </div>

              <Button
                type="submit"
                disabled={isChecking || isRedeeming || !giftCardNumber.trim()}
                className="w-full"
                variant="gradient"
                data-testid="button-check-status"
              >
                {isChecking ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking Status...
                  </>
                ) : (
                  "Check Gift Card Status"
                )}
              </Button>
            </form>

            {/* Card Status Display */}
            {cardStatus && (
              <div className="mt-6 pt-6 border-t">
                {cardStatus.isValid ? (
                  <div className="space-y-4">
                    <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20">
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertDescription className="text-green-800 dark:text-green-200">
                        <div className="font-semibold mb-1">Valid Gift Card</div>
                        <div>Balance: ${cardStatus.balance?.toFixed(2) || '0.00'}</div>
                        <div className="text-sm mt-1">{cardStatus.message}</div>
                      </AlertDescription>
                    </Alert>

                    <Button
                      onClick={handleRedeem}
                      disabled={isRedeeming || cardStatus.balance <= 0}
                      className="w-full"
                      variant="gradient"
                      size="lg"
                      data-testid="button-redeem"
                    >
                      {isRedeeming ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Redeeming...
                        </>
                      ) : (
                        `Redeem $${cardStatus.balance?.toFixed(2) || '0.00'}`
                      )}
                    </Button>
                  </div>
                ) : (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-semibold mb-1">Invalid Gift Card</div>
                      <div>{cardStatus.message}</div>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Redemption History */}
        {user && redemptionsData?.redemptions && redemptionsData.redemptions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Redemption History</CardTitle>
              <CardDescription>
                Your past Cardivo gift card redemptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {redemptionsData.redemptions.map((redemption: any) => (
                  <div
                    key={redemption.id}
                    className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 dark:bg-gray-800"
                    data-testid={`redemption-${redemption.id}`}
                  >
                    <div>
                      <div className="font-medium" data-testid={`text-gift-card-${redemption.id}`}>
                        {redemption.giftCardNumber}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(redemption.redeemedAt || redemption.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600 dark:text-green-400" data-testid={`amount-${redemption.id}`}>
                        ${parseFloat(redemption.amount).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                        {redemption.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
